import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, Loader2, MessagesSquare, Paperclip, RefreshCw } from "lucide-react";

import type { ConversationCursor } from "@/api/router/messages/schemas";
import type { DraftContext } from "@/types/draft";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { MessageBubble } from "@/components/messages/message-bubble";
import { MessageCache } from "@/lib/message-cache";
import { useConversationWindow } from "@/hooks/use-conversation-window";
import { Conversations } from "@/lib/conversation-session";
import { ConversaLabels, CockpitLabels } from "@/lib/labels";
import { djs } from "@/lib/djs";

import { CockpitComposer } from "./cockpit-composer";
import { MessagingStatus } from "@/components/realtime/messaging-status";
import { RegisterExamUpload } from "./register-exam-upload";
import { WindowChip } from "./window-chip";

export function LiveWindow({
	context,
	mediaTotal,
	filesOpen,
	onToggleFiles,
}: {
	context: DraftContext;
	mediaTotal: number;
	filesOpen: boolean;
	onToggleFiles: (() => void) | null;
}) {
	const queryClient = useQueryClient();
	const personId = context.person.id;
	const session = Conversations.forPerson(personId);
	const listRef = useRef<HTMLDivElement>(null);
	const earlierRef = useRef<HTMLDivElement>(null);
	const restored = useRef(false);
	const lastId = useRef(session.reading.lastId);
	const heightBefore = useRef<{ height: number; pages: number } | null>(null);
	const [newBelow, setNewBelow] = useState(false);

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isFetchingNextPage,
		isPending,
		isError,
		refetch,
	} = useInfiniteQuery(
		orpc.messages.conversation.infiniteOptions({
			input: (before: ConversationCursor) => ({ person_id: personId, before }),
			initialPageParam: null as ConversationCursor,
			getNextPageParam: (page) => page.next_before,
			gcTime: 10 * 60 * 1000,
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			refetchOnMount: false,
			meta: { conversation: { personId } },
		}),
	);
	const state = useConversationWindow(data?.pages[0]?.state);

	useEffect(() => {
		if (data && !isFetching) {
			void MessageCache.for(queryClient)
				.person(personId)
				.catch(() => null);
		}
	}, [personId, queryClient, isFetching, !!data]);

	const messages = data ? data.pages.toReversed().flatMap((page) => page.messages) : [];
	const latest = messages.at(-1)?.id;

	useLayoutEffect(() => {
		const el = listRef.current;

		if (!el || !data) {
			return;
		}

		if (!restored.current) {
			el.scrollTop = session.reading.atBottom ? el.scrollHeight : session.reading.top;
			restored.current = true;
		} else if (heightBefore.current !== null && data.pages.length > heightBefore.current.pages) {
			el.scrollTop += el.scrollHeight - heightBefore.current.height;
			heightBefore.current = null;
		} else if (session.reading.atBottom) {
			el.scrollTop = el.scrollHeight;
		}

		if (!session.reading.atBottom && lastId.current !== undefined && lastId.current !== latest) {
			setNewBelow(true);
		}

		lastId.current = latest;
		session.reading = { ...session.reading, top: el.scrollTop, lastId: latest };
	}, [data, latest, session]);

	useEffect(() => {
		const el = listRef.current;

		if (!el) {
			return;
		}

		const observer = new ResizeObserver(() => {
			if (session.reading.atBottom) {
				el.scrollTop = el.scrollHeight;
			}
		});
		observer.observe(el);

		return () => observer.disconnect();
	}, [session]);

	useEffect(() => {
		const list = listRef.current;
		const sentinel = earlierRef.current;

		if (!list || !sentinel || !hasNextPage || isFetching || isError || !data) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries.some((entry) => entry.isIntersecting) || heightBefore.current !== null) {
					return;
				}

				heightBefore.current = { height: list.scrollHeight, pages: data.pages.length };
				fetchNextPage().then((result) => {
					if (result.isError || (result.data?.pages.length ?? 0) <= data.pages.length) {
						heightBefore.current = null;
					}
				});
			},
			{ root: list, rootMargin: "160px 0px 0px 0px" },
		);
		observer.observe(sentinel);

		return () => observer.disconnect();
	}, [data, fetchNextPage, hasNextPage, isError, isFetching]);

	const handleScroll = () => {
		const el = listRef.current;

		if (!el || !restored.current) {
			return;
		}

		session.reading = {
			top: el.scrollTop,
			atBottom: el.scrollHeight - el.scrollTop - el.clientHeight < 80,
			lastId: latest,
		};

		if (session.reading.atBottom) {
			setNewBelow(false);
		}
	};
	const outbound = state?.last_outbound;
	const outboundMinutes = outbound
		? Math.round(djs().diff(djs(outbound.sent_at), "minute", true))
		: null;

	return (
		<div data-component="live-window" className="flex min-h-0 flex-1 flex-col">
			<div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border/60 px-4 py-2">
				<div className="flex flex-wrap gap-1.5">
					{state && (
						<>
							<WindowChip open={state.window_open} hoursLeft={state.window_hours_left} />
							<MessagingStatus />
						</>
					)}
				</div>
				<div className="flex items-center gap-2">
					{onToggleFiles && (
						<Button
							size="sm"
							variant="ghost"
							data-slot="files-toggle"
							aria-pressed={filesOpen}
							onClick={onToggleFiles}
						>
							<Paperclip />
							{ConversaLabels.mediaCount(mediaTotal)}
						</Button>
					)}
					<RegisterExamUpload personId={personId} />
				</div>
			</div>
			<div className="relative min-h-0 flex-1">
				<div
					ref={listRef}
					onScroll={handleScroll}
					className="h-full overflow-y-auto bg-background/40 p-4 [overflow-anchor:none]"
				>
					{isPending && (
						<div className="animate-pulse space-y-4">
							<div className="h-16 w-3/4 rounded-lg bg-border/40" />
							<div className="ml-auto h-20 w-2/3 rounded-lg bg-primary-soft" />
							<p className="text-xs text-muted-foreground">{ConversaLabels.loading}</p>
						</div>
					)}
					{isError && (
						<div role="alert" className="mb-4 space-y-2 text-sm text-destructive">
							<p>{ConversaLabels.error}</p>
							<Button size="sm" variant="outline" onClick={() => refetch()}>
								<RefreshCw />
								Tentar de novo
							</Button>
						</div>
					)}
					{hasNextPage && (
						<div
							ref={earlierRef}
							role="status"
							className="mb-2 flex h-6 justify-center text-muted-foreground"
						>
							{isFetchingNextPage && (
								<>
									<Loader2 className="size-4 animate-spin" aria-hidden="true" />
									<span className="sr-only">Carregando mensagens anteriores</span>
								</>
							)}
						</div>
					)}
					<ol className="flex flex-col gap-3">
						{messages.map((message) => (
							<MessageBubble key={message.id} message={message} />
						))}
					</ol>
					{!isPending && !isError && messages.length === 0 && (
						<EmptyState icon={MessagesSquare} title={ConversaLabels.empty} />
					)}
				</div>
				{newBelow && (
					<Button
						size="sm"
						className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full shadow-control"
						onClick={() => {
							if (listRef.current) {
								listRef.current.scrollTop = listRef.current.scrollHeight;
							}
							session.reading.atBottom = true;
							setNewBelow(false);
						}}
					>
						{ConversaLabels.newMessages}
						<ArrowDown />
					</Button>
				)}
			</div>
			{outbound && outboundMinutes !== null && outboundMinutes < 10 && (
				<p data-slot="last-outbound" className="shrink-0 px-4 py-1 text-xs text-muted-foreground">
					{CockpitLabels.lastOutbound(outbound.author, outboundMinutes)}
				</p>
			)}
			<CockpitComposer context={context} state={state} />
		</div>
	);
}
